import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from '../../routing';
import { useAuth } from '../../context/AuthContext';
import { commandCenterApi } from '../../utils/api';
import Icon from '../../components/Icon';
import { toast } from '../../components/Toast';
import CommandCenterSearch from './CommandCenterSearch';
import { DailyMessageModal, DailyMessageEditor } from './DailyMessage';
import mermaidRedhead from '../../assets/peak-mermaid-redhead.svg';
import './commandCenter.css';

// The Command Center's shell — a SIBLING of the customer app's layout, not a
// page inside it. Three things follow from that, and all three are the point:
//
//   • No Navbar, therefore no TenantChip. A restaurant switcher here would be
//     actively lying: nothing in this console is scoped to one restaurant.
//   • No <main key={restaurantId}> wrapper. Console pages must not remount
//     because some other tab switched customer.
//   • It has no tenant. Handoffs to the customer app pass the
//     explicit restaurant URL to a new tab. Existing customer tabs retain
//     their own URLs; storage is only a hint for legacy destinations.
//
// The pattern (a layout chosen by route, outside the main one) is the same one
// App.js already uses to suppress the navbar on the auth screens.

export const SECTIONS = [
  { to: '/command-center',           label: 'מבט על',          end: true },
  // Peak's own books — the only section here that is not about a customer's
  // work. Second, right after the home screen (owner, 14/09/2026): מבט על now
  // carries a מצב העסק card that routes here, and the section it opens should
  // sit next to the screen that points at it.
  { to: '/command-center/business',  label: 'BE העסק' },
  { to: '/command-center/people',    label: 'משתמשים' },
  { to: '/command-center/customers', label: 'לקוחות' },
  { to: '/command-center/queues',    label: 'לטיפול' },
  // Right after the backlog (owner, 27/09/2026): the queues say what is stuck
  // today, the statistics say where the volume is — which decides what to
  // work on first.
  { to: '/command-center/stats',     label: 'סטטיסטיקות' },
  // Marking document lines (#286) — data upkeep, not a daily queue.
  { to: '/command-center/lines',     label: 'שורות מסמכים' },
];

// Tab title per route. The section name comes SECOND so the shared prefix
// doesn't push the useful part off a narrow tab — and on detail pages the
// entity name comes first, because that's what you're hunting for in the strip.
export function titleFor(pathname) {
  if (pathname.startsWith('/command-center/people/'))    return 'משתמש · Command Center';
  if (pathname.startsWith('/command-center/customers/')) return 'לקוח · Command Center';
  if (pathname.startsWith('/command-center/people'))     return 'Command Center · משתמשים';
  if (pathname.startsWith('/command-center/customers'))  return 'Command Center · לקוחות';
  if (pathname.startsWith('/command-center/queues'))     return 'Command Center · לטיפול';
  if (pathname.startsWith('/command-center/stats'))      return 'Command Center · סטטיסטיקות';
  if (pathname.startsWith('/command-center/business'))   return 'Command Center · BE העסק';
  if (pathname.startsWith('/command-center/lines'))      return 'Command Center · שורות מסמכים';
  return 'Command Center · מבט על';
}

// The customer app and the console are one SPA served from one index.html, so
// the tab icon can't be declared statically — it has to be swapped at runtime
// and handed back when you leave.
//
// This is doing real work, not decoration: "Command Center" truncates to
// "Command Ce…" in a narrow tab and the section suffix never survives, so with
// an identical icon the two tabs are indistinguishable — and one of them holds
// a delete-user button while the other holds customer invoices.
//
// Every <link rel="icon"> is swapped (index.html declares 32 and 64) plus the
// apple-touch icon, which is what an "add to home screen" from the console
// would use. The previous hrefs are captured on mount and restored on unmount;
// a hard navigation reloads index.html and resets them anyway.
const CC_ICONS = {
  '32x32': '/favicon-cc-32.png',
  '64x64': '/favicon-cc-64.png',
};

function useCommandCenterFavicon() {
  useEffect(() => {
    const links = [...document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]')];
    const previous = links.map(l => [l, l.getAttribute('href')]);

    for (const link of links) {
      if (link.rel === 'apple-touch-icon') {
        link.setAttribute('href', '/apple-touch-icon-cc.png');
        continue;
      }
      // Fall back to the 64 for any icon link without a sizes attribute.
      const next = CC_ICONS[link.getAttribute('sizes')] || CC_ICONS['64x64'];
      link.setAttribute('href', next);
    }

    return () => {
      for (const [link, href] of previous) {
        if (href == null) link.removeAttribute('href');
        else link.setAttribute('href', href);
      }
    };
  }, []);
}

// Asks when the tab comes to the front, not before: a console opened behind
// another tab may wait all day, by which time the note may have been seen
// elsewhere. The recipient keeps asking on every return, because a console left
// open overnight has a new day's note and no reload to fetch it. Anyone else is
// done after one answer. Decorative, so a failure stays out of the way: the
// console works the same without the note, and the next return tries again.
function useDailyMessage() {
  const [message, setMessage] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const mounted = useRef(true);
  const asking = useRef(false);
  const answered = useRef(false);
  const finished = useRef(false);
  // A read while the note is up would answer null and close it.
  const onScreen = Boolean(message);
  const onScreenRef = useRef(onScreen);
  onScreenRef.current = onScreen;

  const ask = useCallback(async () => {
    asking.current = true;
    try {
      const { data } = await commandCenterApi.getTodaysDailyMessage();
      if (!mounted.current) return;
      answered.current = true;
      finished.current = data.can_edit;
      setMessage(data.message);
      setCanEdit(data.can_edit);
    } finally {
      asking.current = false;
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    const onFront = () => {
      if (document.visibilityState !== 'visible') return;
      if (asking.current || finished.current || onScreenRef.current) return;
      ask().catch(err => console.error('[daily-message] loading failed', err));
    };
    document.addEventListener('visibilitychange', onFront);
    onFront();
    return () => {
      mounted.current = false;
      document.removeEventListener('visibilitychange', onFront);
    };
  }, [ask]);

  // Runs after the modal has rendered, so what gets recorded is a showing that
  // happened. If this report fails the note stays up, and comes again on the
  // next return: seen twice beats not seen at all.
  useEffect(() => {
    if (!onScreen) return;
    commandCenterApi.markDailyMessageShown()
      .then(({ data }) => { if (!data.first) setMessage(null); })
      .catch(err => console.error('[daily-message] reporting the showing failed', err));
  }, [onScreen]);

  // The menu's editor item depends on the answer, so a load that failed gets
  // another try here, and says so if that fails too. The wording stays general:
  // this can be the recipient's screen.
  const retryIfUnanswered = () => {
    if (answered.current || asking.current) return;
    ask().catch((err) => {
      console.error('[daily-message] loading failed', err);
      toast('חלק מהתפריט לא נטען. נסו שוב.', 'error');
    });
  };

  return { message, canEdit, dismiss: () => setMessage(null), retryIfUnanswered };
}

function AccountButton({ onEditDailyMessage, onMenuOpen }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Deliberately NOT the customer app's UserMenu: that menu shows the active
  // restaurant, which is exactly the context this console does not have.
  const initials = (user?.email || '??').replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase() || '??';

  return (
    <div className="user-menu-wrapper">
      <button
        className="user-avatar"
        aria-label="תפריט משתמש"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => { if (!open) onMenuOpen(); setOpen(!open); }}
      >
        {initials}
      </button>
      {open && (
        <>
          <div className="menu-backdrop" onClick={() => setOpen(false)} />
          <div className="user-menu">
            <div className="user-menu-header">
              <div className="user-menu-email" title={user?.email}>{user?.email}</div>
              <div className="user-menu-meta"><span className="badge badge-blue">אדמין</span></div>
            </div>
            <div className="user-menu-section">
              <a className="user-menu-readonly" href="/suppliers" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon name="external" size={13} /> חזרה לאפליקציה
              </a>
            </div>
            {onEditDailyMessage && (
              <div className="user-menu-section">
                <button
                  type="button"
                  className="user-menu-readonly cc-daily-edit"
                  onClick={() => { setOpen(false); onEditDailyMessage(); }}
                >
                  <Icon name="heart" size={13} /> הודעה יומית
                </button>
              </div>
            )}
            <button
              className="user-menu-logout"
              onClick={() => { setOpen(false); logout(); navigate('/login', { replace: true }); }}
            >
              יציאה
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function CommandCenterLayout({ children }) {
  const location = useLocation();
  const onOverview = location.pathname === '/command-center';
  // Two screens carry no global search, for opposite reasons. The overview's
  // hero IS the search field, so a second one competes for the same paste.
  // BE העסק is not a lookup screen at all: it reports on Peak's own books over
  // a period, and every customer it knows about is already listed on it — a
  // field that jumps you to a supplier or a document is an exit, not a tool.
  const showSearch = !onOverview && location.pathname !== '/command-center/business';
  const [drawerOpen, setDrawerOpen] = useState(false);
  const daily = useDailyMessage();
  const [editingDaily, setEditingDaily] = useState(false);

  useEffect(() => { document.title = titleFor(location.pathname); }, [location.pathname]);
  // Leaving the console (or logging out) must hand the tab title back.
  useEffect(() => () => { document.title = 'Peak'; }, []);
  useCommandCenterFavicon();

  // Close on navigation, so tapping a section doesn't leave the panel covering
  // the screen you just asked for.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname, location.search]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  return (
    <div className="cc">
      <div className="cc-topbar">
        {/* Mobile only (CSS-hidden ≥900px). Same corner as the customer app's
            hamburger, so the gesture transfers even though this one opens a
            side panel rather than that one's full-width dropdown. */}
        <button
          type="button"
          className="cc-hamburger"
          aria-label={drawerOpen ? 'סגור תפריט' : 'פתח תפריט'}
          aria-expanded={drawerOpen}
          aria-controls="cc-drawer"
          onClick={() => setDrawerOpen(o => !o)}
        >
          <Icon name={drawerOpen ? 'close' : 'menu'} size={20} strokeWidth={2} />
        </button>

        {/* The Peak mermaid, redhead variant — the console's own mark, so ops
            can tell this tab from a customer tab at a glance. It sits on a
            cream chip rather than bare on the bar: the mermaid's tail is the
            brand green, which all but disappears against --peak-green-deep,
            and her outlines are black. A cream ground, not a circle — a circle
            here would echo the account avatar at the far end of the same bar. */}
        <NavLink to="/command-center" className="cc-mark" aria-label="Peak Command Center — דף הבית">
          <span className="cc-logo-chip">
            <img src={mermaidRedhead} alt="" aria-hidden="true" />
          </span>
          {/* Hidden on narrow phones: 145px of a 375px screen — 39% — spent on
              a word the mermaid chip and the tab title already say. */}
          <span className="cc-wordmark">COMMAND CENTER</span>
        </NavLink>

        {drawerOpen && <div className="cc-drawer-backdrop" onClick={() => setDrawerOpen(false)} />}

        <nav id="cc-drawer" className={`cc-topnav${drawerOpen ? ' is-open' : ''}`}>
          {/* The panel covers the hamburger while open, so the X that toggled
              it is unreachable — the close control has to live in here. */}
          <button
            type="button"
            className="cc-drawer-close"
            aria-label="סגור תפריט"
            onClick={() => setDrawerOpen(false)}
          >
            <Icon name="close" size={18} strokeWidth={2} />
          </button>
          {SECTIONS.map(s => (
            <NavLink
              key={s.to}
              to={s.to}
              end={s.end}
              className={({ isActive }) => (isActive ? 'active' : undefined)}
            >
              {s.label}
            </NavLink>
          ))}
        </nav>
        <AccountButton
          onEditDailyMessage={daily.canEdit ? () => setEditingDaily(true) : null}
          onMenuOpen={daily.retryIfUnanswered}
        />
      </div>


      <main className="cc-main">
        {/* Search lives in the CONTENT column, not in the topbar: it is the
            width of the cards it searches and starts on the same edge, so the
            page reads as one column instead of a narrow field floating over a
            wide table. See showSearch above for the two screens that omit it. */}
        {showSearch && (
          <div className="cc-pagesearch"><CommandCenterSearch /></div>
        )}
        {children}
      </main>
      {daily.message && <DailyMessageModal message={daily.message} onClose={daily.dismiss} />}
      {editingDaily && <DailyMessageEditor onClose={() => setEditingDaily(false)} />}
    </div>
  );
}
